import * as React from 'react';

import {HoverPopover} from 'shells/hover-popover/hover-popover';
import type {HoverPopoverLocationEnum} from 'shells/hover-popover/hover-popover';
import {Popover} from 'shells/popover/popover';
import {useRefObserver} from 'shells/utils/hooks/use-ref-observer';

import {LimitedType} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {getUsageFromLimits} from '../../limits/get-usage-from-limits';
import {useGetLimitsByType} from '../../limits/use-get-limits-by-type';

import {AiUsagePopoverContent} from './ai-usage-popover-content';
import {useGetPowerAiSummarizationMonthlyUsage} from './graphql/hooks';
import {generateTitleBody, getLimitsContent} from './helpers';

export type PowerAiVariant = 'timeline' | 'zoom';

type Props = {
    popoverAnchor: any;
    variant: PowerAiVariant;
    // Pass in to disable button when monthly response limit has been hit
    buttonRef?: any;
    // Location of the popover - default is top
    location?: HoverPopoverLocationEnum;
    isTooltip?: boolean;
};

export function AiUsagePopover(props: Props) {
    const {buttonRef, location = 'top'} = props;
    const linkContainerRef = React.useRef(null);

    const hasAiOutcomes = NutClientConfig.hasAiOutcomesFF;

    const {
        limits: outcomesLimits,
        isLoading: isLoadingOutcomes,
        errorMessage: outcomesErrorMessage,
    } = useGetLimitsByType(LimitedType.AI_OUTCOMES, !hasAiOutcomes);

    const {
        monthlyUsage: summariesUsage,
        isLoading: isLoadingSummaries,
        error: summariesError,
    } = useGetPowerAiSummarizationMonthlyUsage();

    const outcomesUsage = getUsageFromLimits(outcomesLimits);

    const monthlyUsage = hasAiOutcomes ? outcomesUsage : summariesUsage;
    const isLoading = hasAiOutcomes ? isLoadingOutcomes : isLoadingSummaries;
    const error = hasAiOutcomes
        ? Boolean(outcomesErrorMessage || (!isLoadingOutcomes && !outcomesUsage))
        : summariesError;

    const {user} = useGetSessionUser();
    const {isHovered} = useRefObserver(props.popoverAnchor, ['isHovered']);

    // Only show link to users with billing access who are not on Business plan or have hit their monthly limit
    const shouldShowBillingLink =
        user &&
        user.permissions.canAccessBilling &&
        (!NutClientConfig.hasPowerAiPlan || (monthlyUsage && monthlyUsage.remaining <= 0));

    const {title, body} = generateTitleBody(props.variant);

    React.useEffect(() => {
        // If buttonRef is passed in, then we can disable it if no responses remain
        if (buttonRef && buttonRef.current) {
            if (!monthlyUsage || (monthlyUsage && monthlyUsage.remaining === 0)) {
                buttonRef.current.disabled = true;
            } else {
                buttonRef.current.disabled = false;
            }
        }
    }, [buttonRef, monthlyUsage]);

    const popoverContent = (
        <AiUsagePopoverContent
            body={body}
            title={title}
            monthlyUsage={monthlyUsage}
            shouldShowBillingLink={Boolean(shouldShowBillingLink)}
            isTooltip={Boolean(props.isTooltip)}
            linkContainerRef={linkContainerRef}
            limitsContent={getLimitsContent(isLoading, monthlyUsage, error)}
        />
    );

    return props.isTooltip && isHovered ? (
        <Popover anchor={props.popoverAnchor.current} overlayStyles={{display: 'none'}}>
            {popoverContent}
        </Popover>
    ) : (
        // @ts-expect-error
        <HoverPopover anchorRef={props.popoverAnchor} location={location}>
            {popoverContent}
        </HoverPopover>
    );
}
